import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Building, Calendar, Check, Clock, CreditCard, FileText, GraduationCap, IndianRupee, Mail, Monitor, Phone, Upload, User, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import qrCodeImage from '../assets/images/QR code gpay.jpeg';
import logo from '../logo/lo.png';

// Registration + emails now go straight to the Node/CRM server (Brevo emails,
// PDF receipt, Unassigned Leads) instead of Google Apps Script.
const API_BASE = 'https://vistatech-serversss-production.up.railway.app';

// New students pay this fixed amount to register. The rest of the
// before-placement fee is paid later (in full or in installments) via the
// "Existing Student" balance-payment flow. After-placement (the other 50%)
// is handled separately once the student is placed — this form never
// collects it.
const NEW_STUDENT_PAYMENT = 1000;

interface FormData {
  name: string;
  email: string;
  phone: string;
  collegeName: string;
  degree: string;
  yearOfPassout: string;
  course: string;
  sessionCategory: 'online' | 'offline' | '';
  paymentScreenshot: File | null;
  agreesToTerms: boolean;
}

interface FormErrors {
  [key: string]: string;
}

interface SlotData {
  label: string;
}

interface AvailableSlot {
  _id: string;
  label: string;
  sessionCategory: 'online' | 'offline' | 'both';
}

interface BalanceInfo {
  name: string;
  course: string;
  sessionCategory: 'online' | 'offline' | '';
  totalRequired: number;
  totalPaid: number;
  remainingBalance: number;
  isFullyPaid: boolean;
}

const sessionLabel = (sessionCategory: 'online' | 'offline' | '') =>
  sessionCategory === 'offline' ? 'Group Session (Offline)' : 'Group Session (Online)';

// Before-placement fee only — the after-placement 50% (the same amount again)
// is handled separately, once the student is placed.
const getBeforeFee = (sessionCategory: 'online' | 'offline' | '') =>
  sessionCategory === 'offline' ? 20000 : 15000;
const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

// ── Shared dark-theme style helpers ──────────────────────────────────────────
const inp = (err?: string): React.CSSProperties => ({
  width: '100%',
  padding: '11px 12px 11px 36px',
  borderRadius: '10px',
  border: `1.5px solid ${err ? '#f87171' : 'rgba(255,255,255,0.1)'}`,
  background: err ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.05)',
  color: err ? '#fca5a5' : 'white',
  fontSize: '0.88rem',
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
});

const inpNoIcon = (err?: string): React.CSSProperties => ({ ...inp(err), padding: '11px 12px' });

const sel = (err?: string): React.CSSProperties => ({
  ...inp(err),
  cursor: 'pointer',
  appearance: 'none',
  background: err ? 'rgba(248,113,113,0.08)' : '#1a1530',
  paddingRight: '2.5rem',
});

const lbl: React.CSSProperties = {
  display: 'block',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.7rem',
  fontWeight: '700',
  marginBottom: '7px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const errTxt: React.CSSProperties = {
  color: '#f87171',
  fontSize: '0.72rem',
  marginTop: '5px',
  fontWeight: '600',
};

const glassCard: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '22px',
  padding: '32px',
  backdropFilter: 'blur(24px)',
};

const innerSection: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  padding: '18px',
};

// ── Background orbs ─────────────────────────────────────────────────────────
const Orbs = () => (
  <>
    {[
      { s: 500, t: '-180px', l: '-150px', c: 'rgba(78,58,105,0.45)' },
      { s: 380, b: '-120px', r: '-80px',  c: 'rgba(240,180,41,0.12)' },
      { s: 260, t: '45%',   l: '58%',     c: 'rgba(255,101,53,0.1)' },
    ].map((o, i) => (
      <motion.div key={i}
        style={{ position: 'absolute', width: o.s, height: o.s, borderRadius: '50%',
          background: `radial-gradient(circle, ${o.c}, transparent 70%)`,
          top: (o as any).t, left: (o as any).l, right: (o as any).r, bottom: (o as any).b,
          pointerEvents: 'none' }}
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 10 + i * 4, repeat: Infinity, ease: 'easeInOut' }}
      />
    ))}
  </>
);

const PageShell: React.FC<{ children: React.ReactNode; center?: boolean }> = ({ children, center }) => (
  <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg, #2b2240 0%, #0d0b14 60%, #1a0b24 100%)', position: 'relative', overflow: 'hidden', display: center ? 'flex' : undefined, alignItems: center ? 'center' : undefined, justifyContent: center ? 'center' : undefined, padding: center ? '40px 1.5rem' : undefined }}>
    <Orbs />
    {children}
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const GroupSessionPaymentForm: React.FC = () => {
  const [mode, setMode] = useState<'choice' | 'new' | 'existing'>('choice');
  const [currentStep, setCurrentStep] = useState<'registration' | 'slot-selection' | 'completion'>('registration');

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    collegeName: '',
    degree: '',
    yearOfPassout: '',
    course: '',
    sessionCategory: '',
    paymentScreenshot: null,
    agreesToTerms: false,
  });

  const [slotData, setSlotData] = useState<SlotData>({ label: '' });
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const toastTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Existing Student (balance payment) state ──
  const [existingEmail, setExistingEmail] = useState('');
  const [checkingBalance, setCheckingBalance] = useState(false);
  const [balanceInfo, setBalanceInfo] = useState<BalanceInfo | null>(null);
  const [balanceNotFound, setBalanceNotFound] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceScreenshot, setBalanceScreenshot] = useState<File | null>(null);
  const [balanceErrors, setBalanceErrors] = useState<FormErrors>({});
  const [balanceSubmitting, setBalanceSubmitting] = useState(false);
  const [balanceResult, setBalanceResult] = useState<{ totalPaid: number; remainingBalance: number; isFullyPaid: boolean } | null>(null);

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    setToast({ type, message });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  // Slot choices are admin-configured, and each course has its own — only
  // fetch once we reach the schedule step, for the course the student picked.
  useEffect(() => {
    if (currentStep !== 'slot-selection') return;
    setSlotsLoading(true);
    const params = new URLSearchParams({ sessionCategory: formData.sessionCategory, course: formData.course });
    fetch(`${API_BASE}/api/session-slots?${params}`)
      .then(res => res.json())
      .then(result => {
        const slots: AvailableSlot[] = result.ok ? result.slots : [];
        setAvailableSlots(slots);
        // A time picked before going back and changing course may not be offered for the new one
        setSlotData(prev => slots.some(s => s.label === prev.label) ? prev : { label: '' });
      })
      .catch(() => setAvailableSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [currentStep, formData.sessionCategory, formData.course]);

  const courseOptions = [
    { value: '', label: 'Select a course' },
    { value: 'full stack analytics', label: 'Full Stack Analytics' },
    { value: 'AIML', label: 'AI & ML Development' },
  ];

  const BEFORE_PLACEMENT_FEE = getBeforeFee(formData.sessionCategory);

  const termsAndConditions = [
    "The duration of the course is 3 month validity",
    "Students are required to complete all assessments. Unexcused absences from these assessments will be subject to appropriate action.",
    "Following the initial payment, the remaining balance is due in full on the program's official start date. A 48-hour grace period is granted for any outstanding balance.",
    "All official updates and program information will be disseminated via email. Students are responsible for checking their email regularly. The management is not responsible for missed communications due to unchecked email accounts.",
    "Students are required to provide timely updates on the completion of their daily tasks, adhering to established deadlines.",
    "Students experiencing difficulty comprehending new concepts are required to notify the management within 48 hours of the concept's introduction. Failure to do so within this timeframe absolves the management of responsibility for addressing the learning challenges.",
    "Internship will be only scheduled depends upon your assessment performance",
    "Consistent performance below expectations in assessments may necessitate an extended timeframe for program completion.",
    "Consistent communication with the management is mandatory throughout the duration of each project, beginning with the project's start. The management will not be held accountable for errors or necessary updates identified post-project completion if regular progress updates have not been provided",
    "Any instance of academic dishonesty or malpractice during assessments will be subject to disciplinary action, up to and including termination from the program.",
    "Attendance at all scheduled interviews is mandatory. Failure to attend will result in consequences as determined by the program.",
    "Students are required to sign the daily work done sheet only upon demonstrating comprehension of the daily topics covered.",
    "Students will be certified as having completed the course only upon successful completion and submission of all required daily tasks, assessments and projects. Fulfilment of this requirement is mandatory for course completion.",
    `If the student secures a job through Vista Tech's placement support, the remaining after-placement fee (equal to the before-placement amount) must be paid within 10 working days of receiving the offer letter. Failure to pay the remaining amount after placement may result in certificate withholding and potential legal action.`,
    "Internship and Live Project enable to the candidates depends upon their academic performance",
    "In assessment performance, students must obtain a minimum of 50 marks.",
    "Placement support includes resume building, mock interviews, referrals, and interview scheduling.",
    "Two consecutive days of leave are not permitted in this program. Any leave requests must be submitted with prior notice",
    "Once the timing of the batch is finalized, it will be never changed according to the candidates",
    "Prior to the commencement of class, students are requested to arrange themselves comfortably, regardless of the class format (online or offline)",
    "For the duration of online classes, students are expected to remain present. Leaving the online classroom before the scheduled end time will result in an absence being recorded.",
    `The initial ${inr(NEW_STUDENT_PAYMENT)} registration payment is non-refundable under any circumstances. ${formData.sessionCategory
      ? `The remaining ${inr(BEFORE_PLACEMENT_FEE - NEW_STUDENT_PAYMENT)} of the ${inr(BEFORE_PLACEMENT_FEE)} before-placement fee`
      : `The rest of the before-placement fee (${inr(getBeforeFee('online'))} online, ${inr(getBeforeFee('offline'))} offline)`} is due once classes begin and may be paid in installments.`,
    "Upon fulfillment of the aforementioned terms and circumstances, the firm offers a 100% placement"
  ];

  const validateForm = (requireScreenshot: boolean): boolean => {
    const newErrors: FormErrors = {};
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    if (!name) newErrors.name = 'Name is required';
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Please enter a valid email address';
    if (!phone) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) newErrors.phone = 'Please enter a valid 10-digit phone number';
    if (!formData.collegeName.trim()) newErrors.collegeName = 'College name is required';
    if (!formData.degree.trim()) newErrors.degree = 'Degree is required';
    if (!formData.yearOfPassout.trim()) newErrors.yearOfPassout = 'Year of passout is required';
    else {
      const year = parseInt(formData.yearOfPassout, 10);
      const currentYear = new Date().getFullYear();
      if (year < 1950 || year > currentYear + 4) newErrors.yearOfPassout = 'Please enter a valid year';
    }
    if (!formData.course) newErrors.course = 'Please select a course';
    if (!formData.sessionCategory) newErrors.sessionCategory = 'Please select a session type';
    if (!formData.agreesToTerms) newErrors.agreesToTerms = 'You must agree to the terms and conditions';
    if (requireScreenshot && !formData.paymentScreenshot) newErrors.paymentScreenshot = 'Payment screenshot is required';

    setErrors(newErrors);
    const errorKeys = Object.keys(newErrors);
    if (errorKeys.length > 0) {
      showToast('error', newErrors[errorKeys[0]]);
      const el = document.querySelector(`[name="${errorKeys[0]}"]`);
      if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return errorKeys.length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, paymentScreenshot: file }));
    if (errors.paymentScreenshot) setErrors(prev => ({ ...prev, paymentScreenshot: '' }));
  };

  const handlePaymentClick = () => {
    if (validateForm(false)) setShowPayment(true);
  };

  const copyUpiId = () => {
    navigator.clipboard.writeText('9894449002@okbizaxis');
    showToast('success', 'UPI ID copied to clipboard!');
  };
  const copyText = (text: string, label: string) => { navigator.clipboard.writeText(text); showToast('success', `${label} copied!`); };

  const handleSubmit = async () => {
    if (!validateForm(true)) return;
    setIsSubmitting(true);
    try {
      const screenshotBase64 = formData.paymentScreenshot ? await fileToBase64(formData.paymentScreenshot) : null;
      const response = await fetch(`${API_BASE}/api/group-enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          collegeName: formData.collegeName,
          degree: formData.degree,
          yearOfPassout: formData.yearOfPassout,
          course: formData.course,
          sessionCategory: formData.sessionCategory,
          screenshotBase64,
          screenshotFilename: formData.paymentScreenshot?.name || '',
        }),
      });
      const result = await response.json();
      if (result.ok) {
        setCurrentStep('slot-selection');
        showToast('success', result.duplicate ? 'Details updated — pick your session slot below.' : 'Registration completed! Now pick your preferred time slot.');
      } else {
        showToast('error', 'Error submitting registration: ' + (result.error || 'Please try again.'));
      }
    } catch (error) {
      console.error('Submission error:', error);
      showToast('error', 'Error submitting registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitSlotRequest = async () => {
    if (!slotData.label) {
      showToast('error', 'Please select a time slot');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/group-enroll-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone,
          slotPreference: slotData.label,
        }),
      });
      const result = await response.json();
      if (result.ok) {
        setCurrentStep('completion');
        showToast('success', 'Session preference submitted! You will receive an email once your session is confirmed.');
      } else {
        throw new Error(result.error || 'Failed to submit session preference');
      }
    } catch (error) {
      console.error('Error submitting session preference:', error);
      showToast('error', 'Error submitting session preference. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Existing Student handlers ────────────────────────────────────────────────
  const checkBalance = async () => {
    const email = existingEmail.trim();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showToast('error', 'Please enter a valid email address');
      return;
    }
    setCheckingBalance(true);
    setBalanceNotFound(false);
    setBalanceInfo(null);
    try {
      const response = await fetch(`${API_BASE}/api/group-balance-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const result = await response.json();
      if (result.ok && result.found) {
        setBalanceInfo(result);
        setBalanceAmount(result.remainingBalance > 0 ? String(result.remainingBalance) : '');
      } else {
        setBalanceNotFound(true);
      }
    } catch {
      showToast('error', 'Error looking up your balance. Please try again.');
    } finally {
      setCheckingBalance(false);
    }
  };

  const handleBalanceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setBalanceScreenshot(file);
    if (balanceErrors.paymentScreenshot) setBalanceErrors(prev => ({ ...prev, paymentScreenshot: '' }));
  };

  const submitBalancePayment = async () => {
    const newErrors: FormErrors = {};
    const amountNum = Number(balanceAmount);
    if (!balanceAmount || amountNum <= 0) newErrors.amount = 'Enter an amount greater than 0';
    if (!balanceScreenshot) newErrors.paymentScreenshot = 'Payment screenshot is required';
    setBalanceErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showToast('error', Object.values(newErrors)[0]);
      return;
    }
    setBalanceSubmitting(true);
    try {
      const screenshotBase64 = balanceScreenshot ? await fileToBase64(balanceScreenshot) : null;
      const response = await fetch(`${API_BASE}/api/group-balance-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: existingEmail.trim(),
          amount: amountNum,
          screenshotBase64,
          screenshotFilename: balanceScreenshot?.name || '',
        }),
      });
      const result = await response.json();
      if (result.ok) {
        setBalanceResult(result);
        showToast('success', 'Payment received! A receipt has been emailed to you.');
      } else {
        showToast('error', 'Error submitting payment: ' + (result.error || 'Please try again.'));
      }
    } catch (error) {
      console.error('Balance payment error:', error);
      showToast('error', 'Error submitting payment. Please try again.');
    } finally {
      setBalanceSubmitting(false);
    }
  };

  const resetExisting = () => {
    setExistingEmail(''); setBalanceInfo(null); setBalanceNotFound(false);
    setBalanceAmount(''); setBalanceScreenshot(null); setBalanceErrors({}); setBalanceResult(null);
  };

  // ── Toast ────────────────────────────────────────────────────────────────────
  const Toast = () => (
    <AnimatePresence>
      {toast && (
        <motion.div initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }}
          style={{ position: 'fixed', top: '18px', left: '50%', transform: 'translateX(-50%)', zIndex: 100, width: 'min(92vw, 440px)' }}
        >
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '13px 14px', borderRadius: '14px',
            fontFamily: 'Poppins, sans-serif', backdropFilter: 'blur(20px)', boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
            background: toast.type === 'success' ? 'rgba(16,24,20,0.92)' : toast.type === 'error' ? 'rgba(30,16,16,0.92)' : 'rgba(26,21,48,0.92)',
            border: `1px solid ${toast.type === 'success' ? 'rgba(52,211,153,0.4)' : toast.type === 'error' ? 'rgba(248,113,113,0.4)' : 'rgba(240,180,41,0.4)'}`,
          }}>
            <span style={{ fontSize: '1.05rem', flexShrink: 0 }}>{toast.type === 'success' ? '✅' : toast.type === 'error' ? '⚠️' : 'ℹ️'}</span>
            <p style={{ margin: 0, flex: 1, color: 'white', fontSize: '0.82rem', lineHeight: 1.5 }}>{toast.message}</p>
            <button type="button" onClick={() => setToast(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '2px', flexShrink: 0 }}>
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ── Page header (shared across choice / new / existing) ─────────────────────
  const PageHeader = ({ subtitle }: { subtitle: string }) => (
    <div style={{ textAlign: 'center', marginBottom: '36px' }}>
      <img src={logo} alt="Vista Tech" style={{ height: '44px', objectFit: 'contain', marginBottom: '18px' }} />
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.25)', marginBottom: '16px' }}>
        <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }}
          style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F0B429', display: 'inline-block' }} />
        <span style={{ fontSize: '0.68rem', fontWeight: '800', color: '#F0B429', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Vista Tech — Official Enrollment Portal
        </span>
      </div>
      <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: '900', color: 'white', lineHeight: 1.1, marginBottom: '10px' }}>
        50%{' '}
        <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Pay After</span>
        {' '}Placement
      </motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
        style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem' }}>
        {subtitle}
      </motion.p>
    </div>
  );

  // ── Terms Modal ─────────────────────────────────────────────────────────────
  const TermsModal = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem', background: 'rgba(13,11,20,0.9)', backdropFilter: 'blur(10px)' }}
    >
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ background: '#1a1530', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', maxWidth: '720px', width: '100%', maxHeight: '88vh', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.6)', fontFamily: 'Poppins, sans-serif' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'linear-gradient(135deg, #2b2240, #4e3a69)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color="white" />
            </div>
            <div>
              <p style={{ color: 'white', fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>Terms & Conditions</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: 0 }}>Vista Tech Pay After Placement Program</p>
            </div>
          </div>
          <button onClick={() => setShowTermsModal(false)}
            style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} color="white" />
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '62vh' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {termsAndConditions.map((term, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.02)' }}>
                <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '6px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.65rem', fontWeight: '900', marginTop: '1px' }}>{i + 1}</span>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', lineHeight: 1.65, margin: 0 }}>{term}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '16px', padding: '14px', borderRadius: '12px', background: 'rgba(240,180,41,0.07)', border: '1px solid rgba(240,180,41,0.2)', textAlign: 'center' }}>
            <p style={{ color: '#F0B429', fontSize: '0.78rem', margin: 0 }}>
              Full terms at{' '}
              <a href="https://vistatech.org/Terms-and-Conditions" target="_blank" rel="noopener noreferrer" style={{ fontWeight: '700', color: '#F0B429' }}>
                vistatech.org/Terms-and-Conditions
              </a>
            </p>
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={() => setShowTermsModal(false)}
            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.9rem', color: '#0d0b14', background: 'linear-gradient(135deg, #F0B429, #FF6535)' }}>
            I Understand & Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  // ── Choice screen ────────────────────────────────────────────────────────────
  const ChoiceScreen = () => (
    <PageShell>
      {Toast()}
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        {PageHeader({ subtitle: "Launch your tech career — pay half only after you're placed" })}
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <motion.button type="button" whileHover={{ scale: 1.02, y: -3 }} whileTap={{ scale: 0.98 }}
              onClick={() => setMode('new')}
              style={{ ...glassCard, padding: '26px 20px', textAlign: 'left', cursor: 'pointer', fontFamily: 'Poppins, sans-serif' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '11px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>
                <GraduationCap size={20} color="white" />
              </div>
              <p style={{ color: 'white', fontWeight: '800', fontSize: '1rem', margin: '0 0 6px' }}>New Student</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', lineHeight: 1.5, margin: '0 0 10px' }}>Register with ₹{NEW_STUDENT_PAYMENT.toLocaleString('en-IN')} and pick your course &amp; slot.</p>
              {(['online', 'offline'] as const).map(m => (
                <p key={m} style={{ color: '#F0B429', fontSize: '0.74rem', fontWeight: '700', lineHeight: 1.5, margin: 0 }}>
                  {m === 'online' ? 'Online' : 'Offline'} · {inr(getBeforeFee(m))} before + {inr(getBeforeFee(m))} after placement
                </p>
              ))}
            </motion.button>
            <motion.button type="button" whileHover={{ scale: 1.02, y: -3 }} whileTap={{ scale: 0.98 }}
              onClick={() => setMode('existing')}
              style={{ ...glassCard, padding: '26px 20px', textAlign: 'left', cursor: 'pointer', fontFamily: 'Poppins, sans-serif' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '11px', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>
                <CreditCard size={20} color="white" />
              </div>
              <p style={{ color: 'white', fontWeight: '800', fontSize: '1rem', margin: '0 0 6px' }}>Existing Student</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', lineHeight: 1.5, margin: 0 }}>Already registered? Pay your before-placement balance — in full or part, at the fee you joined with.</p>
            </motion.button>
          </motion.div>
        </div>
      </div>
    </PageShell>
  );

  // ── Existing Student flow ────────────────────────────────────────────────────
  const ExistingStudentFlow = () => (
    <PageShell>
      {Toast()}
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        {PageHeader({ subtitle: "Pay off your before-placement balance — in full or in parts" })}
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <button type="button" onClick={() => { setMode('choice'); resetExisting(); }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', marginBottom: '14px', padding: 0 }}>
            <ArrowLeft size={13} /> Back
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>
            {balanceResult ? (
              // ── Payment done ──
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Check size={30} color="white" />
                </div>
                <h2 style={{ color: 'white', fontWeight: '900', fontSize: '1.3rem', margin: '0 0 8px' }}>Payment Received!</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '20px' }}>A receipt has been emailed to you.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '14px' }}>
                    <p style={{ ...lbl, marginBottom: '4px' }}>Total Paid</p>
                    <p style={{ color: '#34d399', fontSize: '1.1rem', fontWeight: '900', margin: 0 }}>₹{balanceResult.totalPaid.toLocaleString('en-IN')}</p>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '14px' }}>
                    <p style={{ ...lbl, marginBottom: '4px' }}>Remaining</p>
                    <p style={{ color: balanceResult.remainingBalance > 0 ? '#fbbf24' : '#34d399', fontSize: '1.1rem', fontWeight: '900', margin: 0 }}>
                      {balanceResult.remainingBalance > 0 ? `₹${balanceResult.remainingBalance.toLocaleString('en-IN')}` : 'Fully Paid ✓'}
                    </p>
                  </div>
                </div>
                {balanceResult.remainingBalance > 0 ? (
                  <motion.button type="button" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                    onClick={() => { setBalanceResult(null); setBalanceAmount(String(balanceResult.remainingBalance)); setBalanceScreenshot(null); }}
                    style={{ width: '100%', padding: '13px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.9rem', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14' }}>
                    Pay Remaining Balance
                  </motion.button>
                ) : (
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: 0 }}>Your before-placement fee is fully settled — a mentor will reach out about your schedule.</p>
                )}
              </div>
            ) : balanceInfo ? (
              // ── Pay balance ──
              <div>
                <div style={{ ...innerSection, marginBottom: '20px' }}>
                  <p style={{ ...lbl, marginBottom: '10px' }}>Your Registration</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      ['Name', balanceInfo.name], ['Course', balanceInfo.course],
                      ['Before Placement Fee', `₹${balanceInfo.totalRequired.toLocaleString('en-IN')}`],
                      ['Paid So Far', `₹${balanceInfo.totalPaid.toLocaleString('en-IN')}`],
                    ].map(([k, v]) => (
                      <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                        <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                        <p style={{ color: 'white', fontSize: '0.84rem', fontWeight: '600', margin: 0 }}>{v}</p>
                      </div>
                    ))}
                  </div>
                  {/* The student's own fee — what they joined at, not today's fee for new students. After placement is the same again. */}
                  <div style={{ marginTop: '8px', background: 'rgba(240,180,41,0.07)', border: '1px solid rgba(240,180,41,0.2)', borderRadius: '8px', padding: '10px 12px' }}>
                    <p style={{ ...lbl, marginBottom: '2px', color: '#F0B429' }}>Your Fee Structure</p>
                    <p style={{ color: 'white', fontSize: '0.84rem', fontWeight: '600', margin: 0 }}>
                      ₹{balanceInfo.totalRequired.toLocaleString('en-IN')} before placement + ₹{balanceInfo.totalRequired.toLocaleString('en-IN')} after placement
                    </p>
                  </div>
                  <div style={{ marginTop: '10px', padding: '12px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', textAlign: 'center' }}>
                    <p style={{ ...lbl, marginBottom: '2px', color: '#fca5a5' }}>Remaining Balance</p>
                    <p style={{ color: '#f87171', fontSize: '1.3rem', fontWeight: '900', margin: 0 }}>₹{balanceInfo.remainingBalance.toLocaleString('en-IN')}</p>
                  </div>
                </div>

                {balanceInfo.isFullyPaid ? (
                  <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <p style={{ color: '#34d399', fontWeight: '700', fontSize: '0.9rem' }}>✓ You've already fully paid your before-placement fee!</p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><IndianRupee size={13} /> Amount You're Paying Now (₹)</label>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" value={balanceAmount}
                        onChange={e => { setBalanceAmount(e.target.value.replace(/[^0-9]/g, '')); if (balanceErrors.amount) setBalanceErrors(p => ({ ...p, amount: '' })); }}
                        placeholder="Enter amount" style={inpNoIcon(balanceErrors.amount)}
                        onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                        onBlur={e => { e.target.style.borderColor = balanceErrors.amount ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                      />
                      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', marginTop: '5px' }}>You can pay the full remaining balance or a partial amount.</p>
                      {balanceErrors.amount && <p style={errTxt}>⚠ {balanceErrors.amount}</p>}
                    </div>

                    {/* QR + UPI */}
                    <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d0b14', fontWeight: '900', fontSize: '0.8rem' }}>G</div>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: '600', fontSize: '0.84rem' }}>Pay via GPay / Any UPI App</span>
                      </div>
                      <div style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                          <div style={{ padding: '10px', borderRadius: '14px', background: 'white', boxShadow: '0 4px 24px rgba(240,180,41,0.2)', border: '2px solid rgba(240,180,41,0.3)' }}>
                            <img src={qrCodeImage} alt="Payment QR Code" style={{ width: '160px', height: '160px', objectFit: 'contain', borderRadius: '6px', display: 'block' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          <div>
                            <p style={{ ...lbl, marginBottom: '2px' }}>UPI ID</p>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '700', color: 'white' }}>9894449002@okbizaxis</span>
                          </div>
                          <motion.button type="button" onClick={copyUpiId} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.78rem', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14' }}>
                            Copy
                          </motion.button>
                        </div>
                      </div>
                    </div>

                    {/* Screenshot upload */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Upload size={13} /> Upload Payment Screenshot
                      </label>
                      <div style={{ borderRadius: '10px', border: `1.5px dashed ${balanceErrors.paymentScreenshot ? '#f87171' : balanceScreenshot ? '#34d399' : 'rgba(255,255,255,0.15)'}`, background: balanceErrors.paymentScreenshot ? 'rgba(248,113,113,0.05)' : balanceScreenshot ? 'rgba(52,211,153,0.05)' : 'rgba(255,255,255,0.02)' }}>
                        <input type="file" accept="image/*" onChange={handleBalanceFileChange}
                          style={{ width: '100%', padding: '12px 16px', fontSize: '0.84rem', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', background: 'transparent', outline: 'none', fontFamily: 'Poppins, sans-serif', boxSizing: 'border-box' }} />
                      </div>
                      {balanceErrors.paymentScreenshot && <p style={errTxt}>⚠ {balanceErrors.paymentScreenshot}</p>}
                      {balanceScreenshot && (
                        <p style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399', fontSize: '0.75rem', marginTop: '5px', fontWeight: '600' }}>
                          <Check size={13} /> {balanceScreenshot.name}
                        </p>
                      )}
                    </div>

                    <motion.button type="button" onClick={submitBalancePayment} disabled={balanceSubmitting}
                      whileHover={balanceSubmitting ? {} : { scale: 1.01 }} whileTap={balanceSubmitting ? {} : { scale: 0.98 }}
                      style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: balanceSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: balanceSubmitting ? 'rgba(255,255,255,0.07)' : 'linear-gradient(135deg, #10b981, #059669)', color: balanceSubmitting ? 'rgba(255,255,255,0.25)' : 'white', boxShadow: balanceSubmitting ? 'none' : '0 8px 28px rgba(16,185,129,0.35)' }}
                    >
                      {balanceSubmitting ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Submitting...</> : <><Check size={18} /> Submit Payment</>}
                    </motion.button>
                  </>
                )}
              </div>
            ) : (
              // ── Lookup ──
              <div>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Mail size={13} /> Your Registered Email</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Mail size={15} /></span>
                  <input type="email" value={existingEmail} onChange={e => setExistingEmail(e.target.value)}
                    placeholder="you@example.com" style={inp()}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                    onKeyDown={e => { if (e.key === 'Enter') checkBalance(); }}
                  />
                </div>
                {balanceNotFound && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '10px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
                    <span style={{ fontSize: '1rem' }}>🔍</span>
                    <div>
                      <p style={{ color: '#f87171', fontSize: '0.8rem', fontWeight: '700', margin: '0 0 4px' }}>No open registration found for this email</p>
                      <p style={{ color: 'rgba(248,113,113,0.75)', fontSize: '0.76rem', margin: 0, lineHeight: 1.5 }}>
                        Double-check it, or{' '}
                        <button type="button" onClick={() => { setMode('new'); resetExisting(); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontWeight: '700', textDecoration: 'underline', padding: 0, fontFamily: 'Poppins, sans-serif', fontSize: '0.76rem' }}>
                          register as a new student
                        </button>.
                      </p>
                    </div>
                  </div>
                )}
                <motion.button type="button" onClick={checkBalance} disabled={checkingBalance}
                  whileHover={checkingBalance ? {} : { scale: 1.01 }} whileTap={checkingBalance ? {} : { scale: 0.98 }}
                  style={{ width: '100%', marginTop: '16px', padding: '14px', borderRadius: '12px', border: 'none', cursor: checkingBalance ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: checkingBalance ? 'rgba(255,255,255,0.07)' : 'linear-gradient(135deg, #F0B429, #FF6535)', color: checkingBalance ? 'rgba(255,255,255,0.25)' : '#0d0b14', boxShadow: checkingBalance ? 'none' : '0 8px 28px rgba(240,180,41,0.35)' }}
                >
                  {checkingBalance ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Checking...</> : 'Check My Balance'}
                </motion.button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </PageShell>
  );

  // ── Step Indicator (New Student flow) ────────────────────────────────────────
  const steps = [{ key: 'registration', label: 'Register & Pay' }, { key: 'slot-selection', label: 'Choose Schedule' }, { key: 'completion', label: 'Done' }];

  const StepIndicator = () => {
    const activeIndex = steps.findIndex(s => s.key === currentStep);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {steps.map((s, i) => {
          const isActive = i === activeIndex;
          const isDone = i < activeIndex;
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.68rem', fontWeight: 800,
                  background: isDone ? 'linear-gradient(135deg, #F0B429, #FF6535)' : isActive ? 'rgba(240,180,41,0.15)' : 'rgba(255,255,255,0.06)',
                  border: isActive ? '1.5px solid #F0B429' : '1.5px solid rgba(255,255,255,0.1)',
                  color: isDone ? 'white' : isActive ? '#F0B429' : 'rgba(255,255,255,0.3)'
                }}>
                  {isDone ? <Check size={12} /> : i + 1}
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isActive ? '#F0B429' : isDone ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)' }}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && <div style={{ width: '20px', height: '1.5px', background: isDone ? '#F0B429' : 'rgba(255,255,255,0.1)' }} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // ── Session Selection Page ──────────────────────────────────────────────────
  const SessionSelectionPage = () => (
    <PageShell>
      {Toast()}
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img src={logo} alt="Vista Tech" style={{ height: '44px', objectFit: 'contain', marginBottom: '18px' }} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', marginBottom: '16px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
            <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#4ade80', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Registration Confirmed</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', fontWeight: '900', color: 'white', marginBottom: '8px' }}>
            Choose Your{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Schedule</span>
          </motion.h1>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', margin: 0 }}>
            Select your preferred timing for{' '}
            <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: '600' }}>{formData.course}</span>
          </p>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {StepIndicator()}
          <button type="button" onClick={() => setCurrentStep('registration')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', marginBottom: '14px', padding: 0 }}>
            ← Back to your details
          </button>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {formData.sessionCategory === 'offline' ? <Building size={16} color="#F0B429" /> : <Monitor size={16} color="#F0B429" />}
              <span style={{ color: '#F0B429', fontWeight: '700', fontSize: '0.84rem' }}>
                {sessionLabel(formData.sessionCategory)} — Slot Preferences
              </span>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={13} /> Choose Your Time Slot
              </label>
              {slotsLoading ? (
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', padding: '12px 0' }}>Loading available times…</p>
              ) : availableSlots.length === 0 ? (
                <div style={{ ...innerSection, textAlign: 'center', padding: '20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem', margin: 0 }}>No time slots are open right now — our team will reach out to schedule you directly.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(availableSlots.length, 3)},1fr)`, gap: '10px' }}>
                  {availableSlots.map(slot => {
                    const sel2 = slotData.label === slot.label;
                    return (
                      <motion.button key={slot._id} type="button" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.97 }}
                        onClick={() => setSlotData({ label: slot.label })}
                        style={{ padding: '16px 12px', borderRadius: '12px', border: `1.5px solid ${sel2 ? '#F0B429' : 'rgba(255,255,255,0.08)'}`, background: sel2 ? 'rgba(240,180,41,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative', boxShadow: sel2 ? '0 0 0 2px rgba(240,180,41,0.2)' : 'none' }}
                      >
                        {sel2 && (
                          <div style={{ position: 'absolute', top: '8px', right: '8px', width: '18px', height: '18px', borderRadius: '50%', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={10} color="white" />
                          </div>
                        )}
                        <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>🕐</div>
                        <div style={{ fontWeight: '700', fontSize: '0.84rem', color: sel2 ? '#F0B429' : 'rgba(255,255,255,0.75)' }}>{slot.label}</div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {slotData.label && (
              <div style={{ ...innerSection, marginBottom: '24px' }}>
                <p style={{ ...lbl, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={12} /> Confirmation Summary</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    ['Name', formData.name],
                    ['Course', formData.course],
                    ['Phone', formData.phone],
                    ['Session', sessionLabel(formData.sessionCategory)],
                    ['Time', slotData.label],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                      <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                      <p style={{ color: 'white', fontSize: '0.84rem', fontWeight: '600', margin: 0 }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <motion.button
              onClick={submitSlotRequest}
              disabled={!slotData.label || isSubmitting}
              whileHover={!slotData.label || isSubmitting ? {} : { scale: 1.01 }}
              whileTap={!slotData.label || isSubmitting ? {} : { scale: 0.98 }}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: !slotData.label || isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: !slotData.label || isSubmitting ? 'rgba(255,255,255,0.07)' : 'linear-gradient(135deg, #F0B429, #FF6535)', color: !slotData.label || isSubmitting ? 'rgba(255,255,255,0.25)' : '#0d0b14', boxShadow: !slotData.label || isSubmitting ? 'none' : '0 8px 28px rgba(240,180,41,0.35)' }}
            >
              {isSubmitting ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Confirming...</> : <><Check size={18} /> Confirm Session Preference</>}
            </motion.button>
            <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '12px', margin: '12px 0 0' }}>Preferences reviewed within 24–48 hours · Confirmation sent to your email</p>
          </motion.div>
        </div>
      </div>
    </PageShell>
  );

  // ── Completion Page ─────────────────────────────────────────────────────────
  const CompletionPage = () => (
    <PageShell center>
      {Toast()}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '420px', width: '100%' }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', overflow: 'hidden', backdropFilter: 'blur(24px)', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}
        >
          <div style={{ padding: '40px 32px', textAlign: 'center', background: 'linear-gradient(135deg, #2b2240, #4e3a69)' }}>
            <motion.div animate={{ rotate: [0, -8, 8, -8, 0] }} transition={{ delay: 0.3, duration: 0.7 }}
              style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', boxShadow: '0 0 48px rgba(240,180,41,0.5)' }}>
              <Check size={34} color="white" />
            </motion.div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '900', color: 'white', marginBottom: '6px' }}>Request Submitted!</h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem', margin: 0 }}>Awaiting admin approval</p>
          </div>

          <div style={{ padding: '28px 28px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {['Admin reviews your session preference within 24–48 hours', 'Email notification sent upon approval', 'Class joining details delivered to your inbox'].map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', borderRadius: '10px', background: i === 0 ? 'rgba(240,180,41,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${i === 0 ? 'rgba(240,180,41,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '8px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontSize: '0.65rem', fontWeight: '900' }}>{i + 1}</div>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', lineHeight: 1.55, margin: 0 }}>{step}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              {[['Name', formData.name], ['Course', formData.course], ['Session', sessionLabel(formData.sessionCategory)], ['Time', slotData.label], ['Status', 'Pending']].map(([k, v]) => (
                <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                  <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                  <p style={{ color: k === 'Status' ? '#fbbf24' : 'white', fontSize: '0.82rem', fontWeight: '600', margin: 0 }}>{v}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <span style={{ fontSize: '1.1rem' }}>📬</span>
              <p style={{ color: '#fbbf24', fontSize: '0.78rem', lineHeight: 1.55, margin: 0 }}>Check your inbox and spam folder regularly for updates from Vista Tech.</p>
            </div>
            <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', marginTop: '16px', margin: '16px 0 0' }}>Thank you for choosing Vista Tech · support@vistatech.com</p>
          </div>
        </motion.div>
      </div>
    </PageShell>
  );

  // ── Mode / step routing ──────────────────────────────────────────────────────
  // Called as plain functions (not JSX elements) — these are defined inline
  // above and would otherwise get a new identity every render, making React
  // remount the whole subtree (and lose input focus) on every keystroke.
  if (mode === 'choice') return ChoiceScreen();
  if (mode === 'existing') return ExistingStudentFlow();
  if (currentStep === 'slot-selection') return SessionSelectionPage();
  if (currentStep === 'completion') return CompletionPage();

  // ── New Student — Registration Form ─────────────────────────────────────────
  return (
    <PageShell>
      {Toast()}

      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        {PageHeader({ subtitle: "Launch your tech career — pay half only after you're placed" })}

        <div style={{ maxWidth: '580px', margin: '0 auto' }}>
          {StepIndicator()}
          <button type="button" onClick={() => setMode('choice')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', marginBottom: '14px', padding: 0 }}>
            <ArrowLeft size={13} /> Change registration type
          </button>
          <AnimatePresence>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>

              {/* ── Course + Session Type ── */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <GraduationCap size={13} /> Select Your Course
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', zIndex: 1 }}><GraduationCap size={15} /></span>
                  <select name="course" value={formData.course} onChange={handleInputChange}
                    style={sel(errors.course)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.course ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  >
                    {courseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', fontSize: '0.7rem' }}>▾</span>
                </div>
                {errors.course && <p style={errTxt}>⚠ {errors.course}</p>}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={13} /> Session Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                  {[
                    { key: 'online',  icon: <Monitor size={20} />,  label: 'Group Session (Online)',  sub: 'Learn live from anywhere', desc: `Group live sessions with Placement support — ${inr(getBeforeFee('online'))} before placement + ${inr(getBeforeFee('online'))} after placement`, col: '#F0B429', grad: 'linear-gradient(135deg,#F0B429,#FF6535)' },
                    { key: 'offline', icon: <Building size={20} />, label: 'Group Session (Offline)', sub: 'At our training center',    desc: `In-person group sessions with Placement support — ${inr(getBeforeFee('offline'))} before placement + ${inr(getBeforeFee('offline'))} after placement`, col: '#3b82f6', grad: 'linear-gradient(135deg,#3b82f6,#2563eb)' },
                  ].map(({ key, icon, label, sub, desc, col, grad }) => {
                    const active = formData.sessionCategory === key;
                    return (
                      <motion.button key={key} type="button" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.97 }}
                        onClick={() => setFormData(prev => ({ ...prev, sessionCategory: key as any }))}
                        style={{ padding: '16px', borderRadius: '12px', border: `1.5px solid ${active ? col : 'rgba(255,255,255,0.08)'}`, background: active ? `${col}12` : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative', boxShadow: active ? `0 0 0 2px ${col}25, 0 4px 16px ${col}18` : 'none' }}
                      >
                        {active && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                            style={{ position: 'absolute', top: '10px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={10} color="white" />
                          </motion.div>
                        )}
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: active ? grad : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', color: active ? 'white' : 'rgba(255,255,255,0.3)' }}>{icon}</div>
                        <div style={{ fontWeight: '700', fontSize: '0.88rem', color: active ? col : 'rgba(255,255,255,0.7)' }}>{label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{sub}</div>
                        <div style={{ fontSize: '0.68rem', color: active ? `${col}cc` : 'rgba(255,255,255,0.3)', marginTop: '6px', lineHeight: 1.5 }}>{desc}</div>
                      </motion.button>
                    );
                  })}
                </div>
                {errors.sessionCategory && <p style={errTxt}>⚠ {errors.sessionCategory}</p>}
              </div>

              {/* ── Email ── */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Mail size={13} /> Email Address</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Mail size={15} /></span>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                    placeholder="you@example.com" style={inp(errors.email)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.email ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.email && <p style={errTxt}>⚠ {errors.email}</p>}
              </div>

              {/* ── Personal Details ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                <span style={{ ...lbl, margin: 0 }}>Personal Details</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><User size={13} /> Full Name</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><User size={15} /></span>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                    placeholder="Enter your full name" style={inp(errors.name)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.name ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.name && <p style={errTxt}>⚠ {errors.name}</p>}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Phone size={13} /> Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Phone size={15} /></span>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                    placeholder="10-digit mobile number" style={inp(errors.phone)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.phone ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.phone && <p style={errTxt}>⚠ {errors.phone}</p>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Building size={13} /> College</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Building size={15} /></span>
                    <input type="text" name="collegeName" value={formData.collegeName} onChange={handleInputChange}
                      placeholder="College name" style={inp(errors.collegeName)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                      onBlur={e => { e.target.style.borderColor = errors.collegeName ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                    />
                  </div>
                  {errors.collegeName && <p style={errTxt}>⚠ {errors.collegeName}</p>}
                </div>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><BookOpen size={13} /> Degree</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><BookOpen size={15} /></span>
                    <input type="text" name="degree" value={formData.degree} onChange={handleInputChange}
                      placeholder="e.g. B.Tech, BCA" style={inp(errors.degree)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                      onBlur={e => { e.target.style.borderColor = errors.degree ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                    />
                  </div>
                  {errors.degree && <p style={errTxt}>⚠ {errors.degree}</p>}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={13} /> Year of Passout</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Calendar size={15} /></span>
                  <input type="number" name="yearOfPassout" value={formData.yearOfPassout} onChange={handleInputChange}
                    placeholder="e.g. 2025" min="1950" max="2030" style={inp(errors.yearOfPassout)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.yearOfPassout ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.yearOfPassout && <p style={errTxt}>⚠ {errors.yearOfPassout}</p>}
              </div>

              {/* ── Terms ── */}
              <div style={{ padding: '16px', borderRadius: '12px', background: errors.agreesToTerms ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.04)', border: `1.5px solid ${errors.agreesToTerms ? '#f87171' : 'rgba(255,255,255,0.1)'}`, marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input type="checkbox" id="agreesToTerms" name="agreesToTerms" checked={formData.agreesToTerms} onChange={handleInputChange}
                    style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer', accentColor: '#F0B429', flexShrink: 0 }} />
                  <label htmlFor="agreesToTerms" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.84rem', lineHeight: 1.55, cursor: 'pointer' }}>
                    I agree to the{' '}
                    <button type="button" onClick={() => setShowTermsModal(true)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F0B429', fontWeight: '700', fontSize: '0.84rem', fontFamily: 'Poppins, sans-serif', textDecoration: 'underline', padding: 0 }}>
                      Terms & Conditions
                    </button>
                    {' '}of Vista Tech's Pay After Placement program.
                    {formData.agreesToTerms && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', color: '#34d399', fontWeight: '700', fontSize: '0.75rem' }}>
                        <Check size={12} /> Accepted
                      </span>
                    )}
                  </label>
                </div>
                {errors.agreesToTerms && <p style={errTxt}>⚠ {errors.agreesToTerms}</p>}
              </div>

              {/* ── Payment CTA / Payment Panel ── */}
              {!showPayment ? (
                <motion.button type="button" onClick={handlePaymentClick}
                  whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14', boxShadow: '0 8px 28px rgba(240,180,41,0.35)' }}
                >
                  <CreditCard size={20} /> Proceed to Payment →
                </motion.button>
              ) : (
                // ── Payment Panel ──
                <div style={{ ...innerSection, marginTop: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #2b2240, #4e3a69)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IndianRupee size={17} color="white" />
                      </div>
                      <div>
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '0.88rem', margin: 0 }}>Complete Payment</p>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', margin: 0 }}>Secure UPI transfer · Registration Fee</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', margin: 0 }}>Due</p>
                      <p style={{ color: '#F0B429', fontSize: '1.25rem', fontWeight: '900', margin: 0 }}>₹{NEW_STUDENT_PAYMENT.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.74rem', margin: '0 0 16px', lineHeight: 1.6 }}>
                    The remaining ₹{(BEFORE_PLACEMENT_FEE - NEW_STUDENT_PAYMENT).toLocaleString('en-IN')} of the ₹{BEFORE_PLACEMENT_FEE.toLocaleString('en-IN')} before-placement fee is paid later, once classes begin — in full or in parts — via "Existing Student" on this same page.
                  </p>

                  {/* QR + UPI */}
                  <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d0b14', fontWeight: '900', fontSize: '0.8rem' }}>G</div>
                      <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: '600', fontSize: '0.84rem' }}>Pay via GPay / Any UPI App</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                      <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '14px' }}>
                        Scan QR to pay{' '}
                        <span style={{ color: '#F0B429', fontWeight: '900', fontSize: '1.2rem' }}>₹{NEW_STUDENT_PAYMENT.toLocaleString('en-IN')}</span>
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                        <div style={{ padding: '10px', borderRadius: '14px', background: 'white', boxShadow: '0 4px 24px rgba(240,180,41,0.2)', border: '2px solid rgba(240,180,41,0.3)' }}>
                          <img src={qrCodeImage} alt="Payment QR Code" style={{ width: '160px', height: '160px', objectFit: 'contain', borderRadius: '6px', display: 'block' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                        <span style={{ ...lbl, margin: 0 }}>Or Copy UPI ID</span>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <div>
                          <p style={{ ...lbl, marginBottom: '2px' }}>UPI ID</p>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '700', color: 'white' }}>9894449002@okbizaxis</span>
                        </div>
                        <motion.button type="button" onClick={copyUpiId} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.78rem', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14' }}>
                          Copy
                        </motion.button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 10px' }}>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                        <span style={{ ...lbl, margin: 0 }}>Or Bank Transfer</span>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                      </div>
                      <div style={{ padding: '14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        {[['Account Name', 'Vista Tech', false], ['Account Number', '24340200002436', true], ['IFSC Code', 'FDRL0002434', true]].map(([label, value, copyable]) => (
                          <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ ...lbl, margin: 0 }}>{label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: '700', color: 'white' }}>{value}</span>
                              {copyable && (
                                <motion.button type="button" onClick={() => copyText(value as string, label as string)} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                                  style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.7rem', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14' }}>
                                  Copy
                                </motion.button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Screenshot upload */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Upload size={13} /> Upload Payment Screenshot
                    </label>
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', margin: '0 0 8px' }}>
                      Sent to our team to verify your payment.
                    </p>
                    <div style={{ borderRadius: '10px', border: `1.5px dashed ${errors.paymentScreenshot ? '#f87171' : formData.paymentScreenshot ? '#34d399' : 'rgba(255,255,255,0.15)'}`, background: errors.paymentScreenshot ? 'rgba(248,113,113,0.05)' : formData.paymentScreenshot ? 'rgba(52,211,153,0.05)' : 'rgba(255,255,255,0.02)' }}>
                      <input type="file" accept="image/*" onChange={handleFileChange}
                        style={{ width: '100%', padding: '12px 16px', fontSize: '0.84rem', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', background: 'transparent', outline: 'none', fontFamily: 'Poppins, sans-serif', boxSizing: 'border-box' }} />
                    </div>
                    {errors.paymentScreenshot && <p style={errTxt}>⚠ {errors.paymentScreenshot}</p>}
                    {formData.paymentScreenshot && (
                      <p style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399', fontSize: '0.75rem', marginTop: '5px', fontWeight: '600' }}>
                        <Check size={13} /> {formData.paymentScreenshot.name}
                      </p>
                    )}
                  </div>

                  {/* Single submit — validates + registers in one click */}
                  <motion.button type="button" onClick={handleSubmit} disabled={isSubmitting}
                    whileHover={isSubmitting ? {} : { scale: 1.01 }} whileTap={isSubmitting ? {} : { scale: 0.97 }}
                    style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: isSubmitting ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #10b981, #059669)', color: isSubmitting ? 'rgba(255,255,255,0.3)' : 'white', boxShadow: isSubmitting ? 'none' : '0 8px 28px rgba(16,185,129,0.35)' }}
                  >
                    {isSubmitting
                      ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Submitting...</>
                      : <><Check size={20} /> Complete Registration — Pay ₹{NEW_STUDENT_PAYMENT.toLocaleString('en-IN')}</>}
                  </motion.button>
                </div>
              )}

            </motion.div>
          </AnimatePresence>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>
              <span>🔒</span> 256-bit SSL encrypted · Your data is always secure
            </p>
            <p style={{ color: 'rgba(255,255,255,0.12)', fontSize: '0.7rem', marginTop: '5px' }}>© Vista Tech Solutions · Secure Registration Portal</p>
          </div>
        </div>
      </div>

      {showTermsModal && TermsModal()}
    </PageShell>
  );
};

export default GroupSessionPaymentForm;
